import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import MoneyDisplay from 'components/util/MoneyDisplay';
import PercentageDisplay from 'components/util/PercentageDisplay';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useCallback } from 'react';
import { Control, UseFieldArrayRemove, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useDeleteInvoiceRowMutation, useUpdateInvoiceRowMutation } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceRowCorrectionForm, InvoiceRowForm, InvoiceRowTotals } from 'types/invoices';
import { cn } from 'utils/cn';
import { getClassifierText } from 'utils/getTranslations';
import { mapInvoiceRowFormToAdminFeeUpdateDto } from '../../../../util/InvoicesPageUtil';
import { FROZEN_CORRECTION } from '../../details-tab/InvoiceItemRow';
import { TakeActionBackDisplay } from '../invoice-rows/TakeActionBackDisplay';
import { TOTALS_CAPTION, TOTALS_GRID, TOTALS_ROW, TOTALS_TEXT } from '../TotalsTableCard';
import { AdminFeeEditButton } from './AdminFeeEditButton';

interface Props {
    index: number;
    matterId: number;
    invoiceRowTotals?: InvoiceRowTotals;
    invoice: InvoiceData;
    remove: UseFieldArrayRemove;
    isInViewMode: boolean;
    control: Control<InvoiceRowCorrectionForm>;
    setValue: UseFormSetValue<InvoiceRowCorrectionForm>;
    handleUpdateInvoiceRows: (idList: number[]) => void;
    baseAmount: number;
    classifiers: Record<string, ClassifierData>;
}

const AdminFeeTotalsTableItem: React.FC<Props> = (props) => {
    const {
        index,
        matterId,
        remove,
        invoice,
        invoiceRowTotals,
        isInViewMode,
        control,
        setValue,
        handleUpdateInvoiceRows,
        baseAmount,
        classifiers,
    } = props;
    const { isPaid, isSent, isCorrection: isCorrectionInvoice } = useInvoice(invoice);
    const [editIntentTypeCode] = useWatch({
        control,
        name: [`rows.${index}.editIntentTypeCode`],
    });

    const isRemovedViaCorrection = invoiceRowTotals?.isRemoved;
    const isRowDisabled = editIntentTypeCode !== null || isRemovedViaCorrection;
    const canTakeActionBack = !isInViewMode && !isRemovedViaCorrection;
    const hideSelectionCheckbox = !isCorrectionInvoice || isInViewMode || isRowDisabled || isSent || isPaid;

    const invoiceRowId = invoiceRowTotals?.invoiceRowId;
    const totalWoVat = invoiceRowTotals?.totalWoVat || 0;
    const vatAmount = invoiceRowTotals?.vatAmount || 0;
    const vatRate = (invoiceRowTotals?.vatRate || 0) * 100;
    const totalWithVat = invoiceRowTotals?.totalWithVat || 0;
    const totalWithVatPaymentCurrency = invoiceRowTotals?.totalWithVatPaymentCurrency || 0;
    const adminFeeRate = invoiceRowTotals?.adminFeeRate ?? 0;

    const [updateInvoiceRow] = useUpdateInvoiceRowMutation();

    const handleUpdateRowTotals = useCallback(
        async (data: InvoiceRowForm) => {
            await updateInvoiceRow(mapInvoiceRowFormToAdminFeeUpdateDto(data, invoice.id));
        },
        [updateInvoiceRow, invoice.id]
    );

    const [deleteInvoiceRow] = useDeleteInvoiceRowMutation();

    const handleDeleteInvoiceRow = useCallback(
        async (invoiceRowId: number) => {
            await deleteInvoiceRow({ invoiceId: invoice.id, invoiceRowId: Number(invoiceRowId) })
                .unwrap()
                .catch(console.error)
                .finally(() => remove(index));
        },
        [deleteInvoiceRow, index, remove, invoice.id]
    );

    return (
        <div className={cn(TOTALS_ROW, isRowDisabled && FROZEN_CORRECTION)}>
            {isRowDisabled && canTakeActionBack && invoiceRowId ? (
                <TakeActionBackDisplay
                    index={index}
                    invoiceRowId={invoiceRowId}
                    editIntentTypeCode={editIntentTypeCode}
                    setValue={setValue}
                    handleUpdateInvoiceRows={handleUpdateInvoiceRows}
                />
            ) : null}
            <div className={cn(TOTALS_GRID, 'opacity-decreasing-when-disabled items-start')}>
                <div className="col-span-3 flex flex-row items-center gap-2">
                    {hideSelectionCheckbox ? null : (
                        <div className="flex justify-center">
                            <CheckboxInput control={control} name={`rows.${index}.selected`} />
                        </div>
                    )}
                    <div className="flex flex-col gap-2">
                        <p className={cn(TOTALS_TEXT, 'font-semibold')}>{invoiceRowTotals?.description || ''}</p>
                        <p className={TOTALS_CAPTION}>
                            {getClassifierText(classifiers['INVOICE_ROW_TYPE'], invoiceRowTotals?.invoiceRowTypeCode)}
                        </p>
                    </div>
                </div>
                <p className={cn(TOTALS_TEXT, 'col-span-1 text-right')}>
                    <PercentageDisplay value={adminFeeRate} />
                </p>
                <p className={cn(TOTALS_TEXT, 'col-span-2 text-right')}>
                    <MoneyDisplay amount={totalWoVat} />
                </p>
                <div className={cn(TOTALS_TEXT, 'col-span-1 flex flex-col gap-2 text-right')}>
                    <p>
                        <MoneyDisplay amount={vatAmount} />
                    </p>
                    <p className="text-fg-secondary">
                        <PercentageDisplay value={vatRate} />
                    </p>
                </div>
                <p className={cn(TOTALS_TEXT, 'col-span-2 text-right font-semibold')}>
                    <MoneyDisplay amount={totalWithVat} />
                </p>
                <p className={cn(TOTALS_TEXT, 'col-span-2 text-right font-semibold')}>
                    <MoneyDisplay amount={isRowDisabled ? 0 : totalWithVatPaymentCurrency} />
                </p>
                <div className="col-span-1 flex justify-end">
                    {!isInViewMode && !isRowDisabled && invoiceRowTotals ? (
                        <AdminFeeEditButton
                            invoiceRowTotals={invoiceRowTotals}
                            matterId={matterId}
                            invoice={invoice}
                            handleSubmit={(data) => {
                                void handleUpdateRowTotals(data);
                            }}
                            handleDelete={(invoiceRowId) => {
                                void handleDeleteInvoiceRow(invoiceRowId);
                            }}
                            baseAmount={baseAmount}
                            classifiers={classifiers}
                        >
                            <FormattedMessage id="invoices.totals-tab.table-contents.edit-button" />
                        </AdminFeeEditButton>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default AdminFeeTotalsTableItem;
